/**
 * Employee compensation: salary, 13th salary and the internal cost rate that
 * prices the employee's approved project time.
 */
import { useState, type ChangeEvent } from "react";
import { CURRENCIES } from "@offertli/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { CheckboxField, SelectInput } from "../../components/ui/controls.js";
import { apiPatch } from "../../lib/api.js";
import { MutationError, Section } from "../shared.js";
import type { EmployeeProfileData } from "./types.js";

type SalaryType = "monthly" | "hourly";
type SalaryBasis = "full_time" | "actual_pensum";

export function SalarySettingsPanel({
  employee,
  readOnly = false,
}: {
  employee: EmployeeProfileData;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const profile = employee.profile;
  const [draft, setDraft] = useState({
    salaryType: (profile?.salaryType ?? "monthly") as SalaryType,
    salaryAmount: profile?.salaryAmount ?? "",
    salaryCurrency: profile?.salaryCurrency ?? "CHF",
    salaryBasis: (profile?.salaryBasis ?? "full_time") as SalaryBasis,
    hasThirteenthSalary: profile?.hasThirteenthSalary ?? false,
    laborCostRate: profile?.laborCostRate ?? "",
  });
  const set = <K extends keyof typeof draft>(
    key: K,
    value: (typeof draft)[K],
  ) => setDraft((current) => ({ ...current, [key]: value }));
  const save = useMutation({
    mutationFn: () =>
      apiPatch(`/employees/${employee.user.id}/profile`, {
        ...draft,
        salaryAmount: draft.salaryAmount || null,
        laborCostRate: draft.laborCostRate || null,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["employee", employee.user.id] }),
  });
  const money = (key: "salaryAmount" | "laborCostRate") => (
    <Input
      fluid
      inputMode="decimal"
      disabled={readOnly}
      value={draft[key]}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        set(key, event.target.value.replace(",", "."))
      }
    />
  );
  return (
    <Section title={t("employees.compensation")}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t("compensation.salaryType")}>
          <SelectInput
            value={draft.salaryType}
            disabled={readOnly}
            onChange={(value) => set("salaryType", value)}
            options={[
              { value: "monthly", label: t("compensation.monthly") },
              { value: "hourly", label: t("compensation.hourly") },
            ]}
          />
        </Field>
        <Field label={t("compensation.salaryAmount")}>
          {money("salaryAmount")}
        </Field>
        <Field label={t("common.currency")}>
          <SelectInput
            value={draft.salaryCurrency}
            disabled={readOnly}
            onChange={(value) => set("salaryCurrency", value)}
            options={CURRENCIES.map((value) => ({ value, label: value }))}
          />
        </Field>
        <Field label={t("compensation.salaryBasis")}>
          <SelectInput
            value={draft.salaryBasis}
            disabled={readOnly}
            onChange={(value) => set("salaryBasis", value)}
            options={[
              { value: "full_time", label: t("compensation.fullTime") },
              {
                value: "actual_pensum",
                label: t("compensation.effectivePensum"),
              },
            ]}
          />
        </Field>
        <div className="sm:col-span-2">
          <CheckboxField
            id="compensation-thirteenth"
            checked={draft.hasThirteenthSalary}
            disabled={readOnly}
            onChange={(value) => set("hasThirteenthSalary", value)}
            label={t("compensation.thirteenth")}
          />
        </div>
        {!readOnly ? (
          <Field
            label={t("compensation.laborCostRate")}
            hint={t("compensation.laborCostRateHint")}
          >
            {money("laborCostRate")}
          </Field>
        ) : null}
      </div>
      <MutationError error={save.error} />
      {!readOnly ? (
        <Button
          className="mt-5"
          onClick={() => save.mutate()}
          loading={save.isPending}
        >
          {t("common.save")}
        </Button>
      ) : null}
    </Section>
  );
}
